/// <amd-module name="TFS/Services/ReauthenticationDialog" />

import * as React from "react";

import * as Resources from "TFS/Services/Resources";
import { RestErrorHandlerResponse } from "VSS/Platform/Context";
import { useComponentContext } from "VSS/Platform/Layout";
import { Dialog } from "VSSUI/Dialog";
import { ITfsRestErrorHandlerService } from "./TfsRestErrorHandlerService.types";

export interface IReauthenticationDialogProps {
    onDismiss: () => void;
    onReject: (reason?: any) => void;
    onResolve: (value: RestErrorHandlerResponse | PromiseLike<RestErrorHandlerResponse>) => void;
    popupUrl: string;
}

export function ReauthenticationDialog(props: IReauthenticationDialogProps) {
    const context = useComponentContext();
    const [popupState, setPopupState] = React.useState<"opening" | "blocked" | "open" | "verifying" | "verificationFailed">("opening");
    const popupRef = React.useRef<Window | null>(null);
    const intervalRef = React.useRef(-1);
    let verifying = false;

    console.log(`ReauthenticationDialog state: ${popupState}`);

    React.useEffect(() => {
        window.addEventListener("message", messageListener);
        openPopup();
        return () => {
            // this will clean up when the dialog closes
            cleanupPopup();
            window.removeEventListener("message", messageListener);
        };
    }, []);

    return (
        <Dialog
            footerButtonProps={[
                {
                    onClick: () => {
                        cleanupPopup();
                        props.onResolve(RestErrorHandlerResponse.Continue);
                        props.onDismiss();
                    },
                    text: Resources.Cancel
                },
                {
                    onClick: () => {
                        openPopup();
                    },
                    primary: true,
                    text: Resources.OpenPopup
                }
            ]}
            lightDismiss={false}
            onDismiss={() => {
                props.onResolve(RestErrorHandlerResponse.Continue);
                props.onDismiss();
            }}
            titleProps={{ text: Resources.DialogTitle }}
        >
            <p>{Resources.ReauthenticationDialogText}</p>
            {popupState === "blocked" && <p>{Resources.PopupBlockedMessage}</p>}
            {popupState === "verificationFailed" && <p>{Resources.VerificationFailedMessage}</p>}
        </Dialog>
    );

    function messageListener(e: MessageEvent) {
        if (e.data === "readyForAuthUrl") {
            let url = new URL(props.popupUrl);
            let search = new URLSearchParams(url.search);
            popupRef.current?.postMessage({ url: search.get('r') })
        }
        else if (e.data === "reauthenticationPopupOpened") {
            setPopupState("open");
            popupRef.current = e.source as Window;
        } else if (e.data === "reauthenticationPopupClosed") {
            cleanupPopup();
            verify();
        }
    }

    function openPopup() {
        setPopupState("opening");
        if (intervalRef.current > 0) {
            cleanupPopup();
        }

        const popupWidth = 500;
        const popupHeight = 500;
        const popup = window.open(
            props.popupUrl,
            "AzureDevOpsReauthentication",
            `width=${popupWidth},height=${popupHeight},left=${(screen.width - popupWidth) / 2},top=${(screen.height - popupHeight) / 2}`
        );
        console.log("Popped popup: %O", popup);
        popupRef.current = popup;
        if (popup) {
            setPopupState("open");
            intervalRef.current = setInterval(checkIfClosed, 100);
        } else {
            setPopupState("blocked");
        }
    }

    /**
     * Periodically checks to see if the authentication popup has been closed.
     */
    function checkIfClosed() {
        if (popupRef.current?.closed) {
            console.log("Popup closed");
            cleanupPopup();
            verify();
        }
    }

    /**
     * Clean up references to the popup and the interval that checks that it's still there.
     */
    function cleanupPopup() {
        clearInterval(intervalRef.current);
        intervalRef.current = -1;
        popupRef.current?.close();
        popupRef.current = null;
    }

    /**
     * Verify that authentication was successful.
     */
    async function verify() {
        setPopupState("verifying");
        if (verifying) {
            return;
        }
        verifying = true;

        try {
            const errorHandlerService = context.pageContext.getService<ITfsRestErrorHandlerService>("ITfsRestErrorHandlerService");
            const result = await errorHandlerService.verify();

            if (result.success) {
                props.onResolve(RestErrorHandlerResponse.Retry);
                props.onDismiss();
            } else {
                setPopupState("verificationFailed");
            }
        } finally {
            verifying = false;
        }
    }
}
